import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import Seo from "@/components/Seo";
import Header from "./Header";
import Footer from "./Footer";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { getPosts } from "@/content/posts";

export default function Blog() {
  const { t, i18n } = useTranslation();
  const list = useScrollReveal<HTMLDivElement>({ threshold: 0.05 });
  const posts = getPosts(i18n.language);
  const [featured, ...rest] = posts;
  const dateLocale = i18n.language.startsWith("pt") ? "pt-BR" : "en-US";

  const fmtDate = (date: string | undefined, opts: Intl.DateTimeFormatOptions) =>
    date ? new Date(date).toLocaleDateString(dateLocale, opts) : "";

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <Seo title={t("seo.blog.title")} description={t("seo.blog.desc")} path="/blog" />
      <Header />
      <main id="main" tabIndex={-1} className="flex-1 outline-none">
        {/* Hero */}
        <section className="w-full px-6 pt-16 pb-12 md:pt-24 md:pb-16">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between ed-eyebrow text-muted-foreground animate-fade-in">
              <span className="inline-flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand ed-blink" />
                {t("blog.eyebrow")}
              </span>
              <span>{t("blog.essays", { count: posts.length })}</span>
            </div>

            <h1 className="ed-mega mt-10 animate-fade-in">
              <span className="block">{t("blog.line1")}</span>
              <span className="block">
                {t("blog.line2")}
                <span className="text-brand">.</span>
              </span>
            </h1>

            <p className="ed-serif mt-10 text-2xl md:text-4xl max-w-2xl text-foreground/85 animate-fade-in-delay-1">
              {t("blog.sub")}
            </p>
          </div>
        </section>

        {featured && (
          <>
            <div className="max-w-[1400px] mx-auto px-6">
              <div className="border-t border-border" />
            </div>

            {/* Featured post */}
            <section className="w-full px-6 py-14 md:py-20">
              <a
                href={`/blog/${featured.slug}`}
                className="group block max-w-[1400px] mx-auto"
              >
                <div className="flex items-center justify-between ed-eyebrow text-muted-foreground">
                  <span>{t("blog.featured")}</span>
                  <span>{fmtDate(featured.frontmatter.date, { month: "long", day: "numeric", year: "numeric" })}</span>
                </div>

                <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-x-10 gap-y-6">
                  <div className="lg:col-span-8">
                    <h2 className="ed-display transition-colors duration-200 group-hover:text-brand">
                      {featured.frontmatter.title}
                    </h2>
                  </div>

                  <div className="lg:col-span-4 flex flex-col justify-end gap-6">
                    <p className="ed-serif text-2xl md:text-3xl text-foreground/85">
                      {featured.frontmatter.summary}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 ed-eyebrow text-muted-foreground">
                      {(featured.frontmatter.tags ?? []).map((tag, i) => (
                        <span key={tag} className="inline-flex items-center gap-3">
                          {i > 0 && <span aria-hidden className="text-border">/</span>}
                          {tag}
                        </span>
                      ))}
                    </div>
                    <span className="inline-flex items-center gap-2 ed-eyebrow text-foreground">
                      {t("blog.readArticle")}
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>
              </a>
            </section>
          </>
        )}

        {rest.length > 0 && (
          <>
            <div className="max-w-[1400px] mx-auto px-6">
              <div className="border-t border-border" />
            </div>

            {/* Remaining posts - editorial list */}
            <section className="w-full px-6 pb-24 md:pb-32">
              <div ref={list.ref} className="max-w-[1400px] mx-auto">
                <div className="ed-eyebrow text-muted-foreground pt-10 pb-2">
                  {t("blog.more")}
                </div>

                {rest.map((p, i) => (
                  <a
                    key={p.slug}
                    href={`/blog/${p.slug}`}
                    className={`group block border-t border-border py-8 md:py-10 reveal reveal-up reveal-delay-${
                      i + 1
                    } ${list.isVisible ? "visible" : ""}`}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-4 items-baseline">
                      <div className="md:col-span-1 ed-eyebrow text-muted-foreground">
                        0{i + 1}
                      </div>

                      <div className="md:col-span-7">
                        <h3 className="ed-headline transition-colors duration-200 group-hover:text-brand">
                          {p.frontmatter.title}
                        </h3>
                        <p className="ed-serif mt-3 text-xl md:text-2xl text-foreground/80 max-w-2xl">
                          {p.frontmatter.summary}
                        </p>
                      </div>

                      <div className="md:col-span-4 flex flex-col gap-4 md:items-end">
                        <span className="ed-eyebrow text-muted-foreground">
                          {fmtDate(p.frontmatter.date, { month: "short", day: "numeric", year: "numeric" })}
                        </span>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 ed-eyebrow text-muted-foreground md:justify-end">
                          {(p.frontmatter.tags ?? []).map((tag, ti) => (
                            <span key={tag} className="inline-flex items-center gap-3">
                              {ti > 0 && <span aria-hidden className="text-border">/</span>}
                              {tag}
                            </span>
                          ))}
                        </div>
                        <span className="inline-flex items-center gap-2 ed-eyebrow text-foreground">
                          {t("blog.read")}
                          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                        </span>
                      </div>
                    </div>
                  </a>
                ))}

                <div className="border-t border-border" />
              </div>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
